
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRoles } from '@/hooks/useUserRoles';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TeamCostDisplay } from './TeamCostDisplay';

interface TeamGuardEquipmentCostProps {
  teamId: string;
}

export const TeamGuardEquipmentCost = ({ teamId }: TeamGuardEquipmentCostProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { isAdmin } = useUserRoles();
  
  // Only fetch data if the user is an admin
  const { data: guardEquipmentData, isLoading } = useQuery({
    queryKey: ['team-guard-equipment-cost', teamId],
    queryFn: async () => {
      console.log("Fetching guard equipment costs for team:", teamId);
      
      // First get team members
      const { data: members, error: membersError } = await supabase
        .from('team_members')
        .select('profile_id')
        .eq('team_id', teamId);
        
      if (membersError) {
        console.error("Error fetching team members:", membersError);
        throw membersError;
      }
      
      if (!members || members.length === 0) {
        console.log("No members found for team:", teamId);
        return { totalCost: 0, guards: [] };
      }
      
      console.log("Team members found:", members.length);
      
      // Get guard profiles
      const guardIds = members.map(member => member.profile_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name')
        .in('id', guardIds);
        
      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        throw profilesError;
      }
      
      // Equipment tables not yet created - temporarily disabled
      console.log("Equipment feature disabled - returning basic guard list");
      return { 
        totalCost: 0, 
        guards: profiles?.map(profile => ({
          id: profile.id,
          name: profile.full_name || `${profile.first_name} ${profile.last_name}`,
          itemCount: 0,
          totalCost: 0
        })) || [] 
      };
    },
    enabled: isAdmin
  });
  
  if (!isAdmin || isLoading) return null;
  
  const totalCost = guardEquipmentData?.totalCost || 0;
  
  return (
    <>
      <TeamCostDisplay
        cost={totalCost}
        tooltipText="Εξοπλισμός φυλάκων"
        displayLabel="Guards Equip"
        borderClass="border-purple-500"
        textClass="text-purple-700"
        onClick={() => setIsDialogOpen(true)}
      />
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Guard Equipment Costs</DialogTitle>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guard</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {guardEquipmentData?.guards && guardEquipmentData.guards.length > 0 ? (
                  guardEquipmentData.guards.map(guard => (
                    <TableRow key={guard.id}>
                      <TableCell className="font-medium">
                        {guard.name}
                      </TableCell>
                      <TableCell className="text-right">{guard.itemCount}</TableCell>
                      <TableCell className="text-right font-medium">€{guard.totalCost.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                      No equipment assigned to guards in this team
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="bg-muted/50">
                  <TableCell colSpan={2} className="text-right font-bold">Total Cost</TableCell>
                  <TableCell className="text-right font-bold">€{totalCost.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
